<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import type { SimpleImageBlock } from './simpleImageBlock.svelte';
	import type { SbImage } from '../types';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import Blok from '../blok.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import Image from '../components/image.svelte';

	interface Props {
		content: ISbStoryData<{
			column_start: '1' | '2';
			variant: 'fullbleed-left' | 'fullbleed-right' | 'left' | 'right';
			title: string | ISbRichtext;
			description: ISbRichtext;
			asset: Array<SimpleImageBlock>;
			keyfacts: Array<{ name: ISbRichtext; icon: SbImage }>;
		}>['content'];
	}

	let { content }: Props = $props();

	let leftVariant = $derived(['fullbleed-left', 'left'].includes(content.variant));
	let fullbleedVariant = $derived(['fullbleed-left', 'fullbleed-right'].includes(content.variant));
</script>

<article
	use:editable={content}
	class={['relative bg-white py-8 lg:py-17', { 'aspect-9/16 lg:aspect-video': fullbleedVariant }]}
>
	{#if fullbleedVariant}
		<div class="absolute inset-0 z-0">
			{#each content.asset as asset}
				<Blok content={asset} />
			{/each}
		</div>
	{/if}

	<PageGrid16 margin class="relative z-10 h-full" gridClass="h-full">
		{#if !fullbleedVariant}
			<div
				class={[
					'col-span-full row-start-1 lg:col-span-7',
					{
						'lg:col-start-1': content.column_start === '1' && !leftVariant,
						'lg:col-start-2': content.column_start === '2' && !leftVariant,
						'lg:col-end-[17]': content.column_start === '1' && leftVariant,
						'lg:col-end-[16]': content.column_start === '2' && leftVariant
					}
				]}
			>
				<div
					class="aspect-square overflow-hidden rounded-xl bg-brown-6 lg:aspect-5/4 lg:rounded-2xl"
				>
					{#each content.asset as asset}
						<Blok content={asset} />
					{/each}
				</div>
			</div>
		{/if}
		<div
			class={[
				'col-span-full lg:col-span-7 lg:row-start-1 lg:mx-auto lg:flex lg:max-w-[426px] lg:flex-col lg:justify-center',
				{
					'lg:col-start-1': content.column_start === '1' && leftVariant,
					'lg:col-start-2': content.column_start === '2' && leftVariant,
					'lg:col-end-[17]': content.column_start === '1' && !leftVariant,
					'lg:col-end-[16]': content.column_start === '2' && !leftVariant,
					'max-lg:flex max-lg:flex-col max-lg:justify-end': fullbleedVariant
				}
			]}
		>
			<div
				class={[
					'mb-3 text-balance text-[36px]/[112%] tracking-[-.36px] max-lg:mt-5 lg:mb-4 lg:text-[48px]/[120%] lg:tracking-[-.48px]',
					{
						'text-brown-1': !fullbleedVariant,
						'text-white': fullbleedVariant
					}
				]}
			>
				<SbRichtext text={content.title} />
			</div>
			<div
				class={[
					'mb-6 text-balance text-[16px]/[150%] text-brown-4 max-lg:tracking-[-.16px] lg:mb-7',
					{
						'text-brown-4': !fullbleedVariant,
						'text-brown-5': fullbleedVariant
					}
				]}
			>
				<SbRichtext text={content.description} />
			</div>

			<div class="flex flex-wrap gap-x-[50px] gap-y-4 lg:gap-x-9">
				{#each content.keyfacts as keyfact}
					<div
						class={[
							'basis-calc(25%-25px) flex flex-col items-center text-center',
							{ 'text-brown-1': !fullbleedVariant, 'text-white': fullbleedVariant }
						]}
					>
						<Image
							src={keyfact.icon.filename}
							alt={keyfact.icon.alt}
							title={keyfact.icon.title}
							class="mb-2 h-6 w-6"
							width={24}
							height={24}
						/>
						<div class="text-balance text-[16px]/[140%]"><SbRichtext text={keyfact.name} /></div>
					</div>
				{/each}
			</div>
		</div>
	</PageGrid16>
</article>
